/**
 * Perguntas do Mercado Livre (SAC): as não respondidas primeiro, mais antigas no topo, com o anúncio ao lado (foto,
 * preço, estoque) para responder sem sair da tela. Respostas prontas com {produto}, {preco}, {estoque} e {marca}.
 * Antes de enviar, avisa o que o ML costuma moderar (telefone, e-mail, link, redes sociais).
 */
import { useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, MessageSquareReply, Pencil, Plus, RefreshCw, Search, Trash2 } from 'lucide-react';
import { api, brl, dateTime, int } from '../api.ts';
import { ErrorBox, Loading, Tag, Thumb } from '../components/ui.tsx';
import { Modal } from './CatalogSettings.tsx';
import { Head, Pager, useSearchBox, useUrlState } from './MeliChannel.tsx';

interface Listing { title: string; thumbnail: string | null; permalink: string | null; price: number | null; stock: number | null; status: string | null; catalog: boolean; sku: string | null; brand: string | null }
interface Row {
  id: string; accountId: string; account: string | null; questionId: string; itemId: string; text: string; status: string; askedAt: string;
  hold: boolean; suspectedSpam: boolean; answer: string | null; answerStatus: string | null; answeredAt: string | null; answeredBy: string | null;
  answerError: string | null; buyerOtherQuestions: number; listing: Listing | null;
}
interface ResponseTime { total: number | null; weekdaysWorkingHours: number | null; weekdaysExtraHours: number | null; weekend: number | null; salesIncrease: number | null }
interface Page {
  accounts: { id: string; name: string }[];
  counts: { open: number; late: number; oldest: string | null; answeredToday: number; medianMinutes7d: number | null };
  mlResponseTime: ResponseTime | null; maxLength: number; total: number; page: number; pageSize: number; rows: Row[];
}
interface Template { id: string; title: string; body: string; position: number }

/** "há 12 min", "há 3 h", "há 2 dias". */
export function ago(iso: string | null): string {
  if (!iso) return '';
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (m < 60) return `há ${m} min`;
  const h = Math.floor(m / 60);
  if (h < 48) return `há ${h} h${m % 60 && h < 6 ? ` ${m % 60} min` : ''}`;
  return `há ${Math.floor(h / 24)} dias`;
}
const minutes = (m: number | null) => (m == null ? '—' : m < 60 ? `${m} min` : m < 1440 ? `${Math.floor(m / 60)} h ${m % 60 ? `${m % 60} min` : ''}` : `${(m / 1440).toFixed(1).replace('.', ',')} dias`);

/** O que o ML costuma moderar em perguntas e respostas. */
function risks(text: string): string[] {
  const out: string[] = [];
  if (/\b[\w.+-]+@[\w-]+\.[\w.-]+\b/.test(text)) out.push('e-mail');
  if (/\b(?:https?:\/\/|www\.)\S+|\b\w+\.(?:com|net|br|org|ly)\b/i.test(text)) out.push('link');
  if (/(?:\(?\d{2}\)?\s?)?9?\d{4}[-\s.]?\d{4}\b/.test(text.replace(/R\$\s?\d+[.,]\d{2}/g, ''))) out.push('telefone');
  if (/whats\s?app|instagram|facebook|telegram|zap\b/i.test(text)) out.push('rede social ou contato fora do ML');
  return out;
}

const fill = (body: string, l: Listing | null) => body
  .replaceAll('{produto}', l?.title ?? 'o produto')
  .replaceAll('{preco}', l?.price != null ? brl(l.price) : '')
  .replaceAll('{estoque}', l?.stock != null ? String(l.stock) : '')
  .replaceAll('{marca}', l?.brand ?? '');

export function QuestionsPage() {
  const qc = useQueryClient();
  const [params, set] = useUrlState();
  const [search, setSearch] = useSearchBox(params, set);
  const [templatesOpen, setTemplatesOpen] = useState(false);
  const status = params.get('status') ?? 'open';
  const page = Number(params.get('page') ?? 1);
  const filter: Record<string, string> = { status };
  for (const k of ['account', 'q']) if (params.get(k)) filter[k] = params.get(k)!;
  const qs = new URLSearchParams({ ...filter, page: String(page) });
  const data = useQuery({
    queryKey: ['questions', qs.toString()],
    queryFn: () => api<Page>(`/channels/mercadolivre/questions?${qs}`),
    placeholderData: keepPreviousData,
    refetchInterval: status === 'open' ? 30_000 : false,
  });
  const templates = useQuery({ queryKey: ['answer-templates'], queryFn: () => api<Template[]>('/channels/questions/templates') });
  const sync = useMutation({
    mutationFn: () => api<{ open: number }>('/channels/mercadolivre/questions/sync', { method: 'POST', json: params.get('account') ? { accountId: params.get('account') } : {} }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['questions'] }),
  });
  const d = data.data;
  const c = d?.counts;
  const rt = d?.mlResponseTime;

  return (
    <>
      <Head title="Perguntas">
        <p>Perguntas dos compradores nos anúncios do Mercado Livre. Responder rápido vende mais: o ML mostra o tempo de resposta e projeta o aumento de vendas quando fica abaixo de 1 hora.</p>
      </Head>
      <div className="stack-y">
        {data.error ? <ErrorBox error={data.error} /> : !d || !c ? <Loading /> : (
          <>
            {d.accounts.length > 1 && (
              <div className="seg" role="group" aria-label="Conta">
                <button type="button" aria-pressed={!params.get('account')} onClick={() => set({ account: null, page: null })}>Todas as contas</button>
                {d.accounts.map((a) => <button key={a.id} type="button" aria-pressed={params.get('account') === a.id} onClick={() => set({ account: a.id, page: null })}>{a.name}</button>)}
              </div>
            )}
            <div className="kpis">
              <div className={`kpi${c.open ? ' alert' : ''}`}><span className="k-label">Sem resposta</span><span className="k-value">{int(c.open)}</span><span className="k-note">{c.oldest ? `a mais antiga ${ago(c.oldest)}` : 'nenhuma esperando'}</span></div>
              <div className={`kpi${c.late ? ' alert' : ''}`}><span className="k-label">Há mais de 1 hora</span><span className="k-value">{int(c.late)}</span><span className="k-note">acima de 1 h derruba a conversão</span></div>
              <div className="kpi"><span className="k-label">Respondidas hoje</span><span className="k-value">{int(c.answeredToday)}</span><span className="k-note">pela Berytus e pelo ML</span></div>
              <div className="kpi"><span className="k-label">Tempo de resposta (7 dias)</span><span className="k-value">{minutes(c.medianMinutes7d)}</span><span className="k-note">mediana, da pergunta à resposta</span></div>
              {rt && (
                <div className={`kpi${rt.salesIncrease ? ' alert' : ''}`}>
                  <span className="k-label">Tempo medido pelo ML</span><span className="k-value">{minutes(rt.total)}</span>
                  <span className="k-note">{rt.salesIncrease ? `responder em até 1 h: +${rt.salesIncrease}% de vendas` : `dia útil ${minutes(rt.weekdaysWorkingHours)} · noite ${minutes(rt.weekdaysExtraHours)} · fim de semana ${minutes(rt.weekend)}`}</span>
                </div>
              )}
            </div>

            <div className="toolbar">
              <div className="seg" role="group" aria-label="Situação">
                {([['open', `Sem resposta (${int(c.open)})`], ['answered', 'Respondidas'], ['all', 'Todas']] as const).map(([v, l]) => (
                  <button key={v} type="button" aria-pressed={status === v} onClick={() => set({ status: v === 'open' ? null : v, page: null })}>{l}</button>
                ))}
              </div>
              <label className="search">
                <Search size={16} />
                <span className="sr-only">Buscar</span>
                <input className="input" type="search" placeholder="Texto, MLB, título ou SKU" value={search} onChange={(e) => setSearch(e.target.value)} />
              </label>
              <span className="grow" />
              <button type="button" className="btn small" onClick={() => setTemplatesOpen(true)}><MessageSquareReply size={14} /> Respostas prontas ({templates.data?.length ?? 0})</button>
              <button type="button" className="btn small" disabled={sync.isPending} onClick={() => sync.mutate()}><RefreshCw size={14} /> {sync.isPending ? 'Lendo…' : 'Ler agora'}</button>
            </div>
            {sync.error && <ErrorBox error={sync.error} />}

            {d.rows.length === 0 ? (
              <div className="panel"><div className="empty"><b>{status === 'open' ? 'Nenhuma pergunta esperando resposta' : 'Nada por aqui'}</b>{status === 'open' ? 'As novas chegam sozinhas, na hora.' : 'Mude o filtro ou a busca.'}</div></div>
            ) : (
              <div style={{ display: 'grid', gap: 12, opacity: data.isPlaceholderData ? 0.55 : 1 }}>
                {d.rows.map((r) => <QuestionCard key={r.id} r={r} max={d.maxLength} templates={templates.data ?? []} />)}
              </div>
            )}
            <div className="panel flush"><Pager total={d.total} page={page} pageSize={d.pageSize} onPage={(p) => set({ page: p > 1 ? String(p) : null })} /></div>
          </>
        )}
      </div>
      {templatesOpen && <TemplatesDialog templates={templates.data ?? []} max={d?.maxLength ?? 2000} onClose={() => setTemplatesOpen(false)} />}
    </>
  );
}

function QuestionCard({ r, max, templates }: { r: Row; max: number; templates: Template[] }) {
  const qc = useQueryClient();
  const [text, setText] = useState('');
  const open = r.status === 'UNANSWERED';
  const late = open && Date.now() - new Date(r.askedAt).getTime() > 3_600_000;
  const answer = useMutation({
    mutationFn: () => api(`/channels/mercadolivre/questions/${r.id}/answer`, { method: 'POST', json: { text } }),
    onSuccess: () => { setText(''); void qc.invalidateQueries({ queryKey: ['questions'] }); },
  });
  const remove = useMutation({
    mutationFn: () => api(`/channels/mercadolivre/questions/${r.id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['questions'] }),
  });
  const warn = useMemo(() => risks(text), [text]);
  const l = r.listing;
  return (
    <div className="panel" style={{ padding: 14, display: 'grid', gap: 10, borderLeft: late ? '3px solid var(--err)' : open ? '3px solid var(--warn)' : undefined }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <Thumb src={l?.thumbnail ?? null} alt="" />
        <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', lineHeight: 1.35 }}>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{l?.title ?? r.itemId}</span>
          <span className="small muted">
            {l?.permalink ? <a href={l.permalink} target="_blank" rel="noreferrer" className="code">{r.itemId} <ExternalLink size={11} /></a> : <span className="code">{r.itemId}</span>}
            {l?.sku ? ` · ${l.sku}` : ''}{l ? ` · ${brl(l.price)} · estoque ${l.stock ?? '—'}` : ''}{l?.status && l.status !== 'active' ? ` · ${l.status === 'paused' ? 'pausado' : l.status}` : ''}{l?.catalog ? ' · catálogo' : ''}
            {r.account ? ` · ${r.account}` : ''}
          </span>
        </div>
        <span className="small" style={{ color: late ? 'var(--err)' : undefined, whiteSpace: 'nowrap' }} title={dateTime(r.askedAt)}>{ago(r.askedAt)}</span>
      </div>
      <div style={{ background: 'var(--surface-2)', borderRadius: 8, padding: '10px 12px' }}>
        <div style={{ whiteSpace: 'pre-wrap' }}>{r.text || <span className="muted">(pergunta moderada pelo Mercado Livre)</span>}</div>
        <div className="small muted" style={{ marginTop: 4, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {r.buyerOtherQuestions > 0 && <span>o comprador já fez {r.buyerOtherQuestions} outra{r.buyerOtherQuestions > 1 ? 's' : ''} pergunta{r.buyerOtherQuestions > 1 ? 's' : ''}</span>}
          {r.hold && <Tag tone="warn">em espera no ML</Tag>}
          {r.suspectedSpam && <Tag tone="warn">possível spam</Tag>}
          {!open && r.status !== 'ANSWERED' && <Tag tone="off">{STATUS[r.status] ?? r.status}</Tag>}
        </div>
      </div>
      {r.answer != null && r.status === 'ANSWERED' && (
        <div style={{ borderLeft: '3px solid var(--ok)', paddingLeft: 10 }}>
          <div style={{ whiteSpace: 'pre-wrap' }}>{r.answer || <span className="muted">(resposta moderada pelo Mercado Livre)</span>}</div>
          <div className="small muted">
            {r.answeredBy ? `${r.answeredBy} pela Berytus` : 'respondida no Mercado Livre'}{r.answeredAt ? ` · ${dateTime(r.answeredAt)}` : ''}
            {r.answeredAt ? ` · em ${minutes(Math.round((new Date(r.answeredAt).getTime() - new Date(r.askedAt).getTime()) / 60_000))}` : ''}
            {r.answerStatus && r.answerStatus !== 'ACTIVE' ? ` · ${r.answerStatus === 'BANNED' ? 'moderada pelo ML' : 'desativada'}` : ''}
          </div>
        </div>
      )}
      {open && (
        <div style={{ display: 'grid', gap: 6 }}>
          {r.answerError && <div className="small" style={{ color: 'var(--err)' }}>Última tentativa recusada: {r.answerError}</div>}
          <textarea className="input" rows={3} maxLength={max} placeholder="Escreva a resposta…" value={text} onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter' && text.trim()) answer.mutate(); }} style={{ resize: 'vertical', minHeight: 76, height: 'auto', padding: '8px 10px' }} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {templates.length > 0 && (
              <select className="select" aria-label="Resposta pronta" value="" onChange={(e) => { const t = templates.find((x) => x.id === e.target.value); if (t) setText((v) => (v ? `${v}\n` : '') + fill(t.body, l)); }} style={{ maxWidth: 260 }}>
                <option value="">Resposta pronta…</option>
                {templates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
              </select>
            )}
            <span className="small muted">{text.length}/{max}</span>
            {warn.length > 0 && <span className="small" style={{ color: 'var(--warn)' }}>O ML costuma moderar: {warn.join(', ')}.</span>}
            <span className="grow" style={{ flex: 1 }} />
            <button type="button" className="btn small ghost" disabled={remove.isPending} title="Apaga a pergunta do anúncio no Mercado Livre"
              onClick={() => { if (window.confirm('Apagar esta pergunta do anúncio no Mercado Livre?')) remove.mutate(); }}><Trash2 size={14} /> Apagar</button>
            <button type="button" className="btn primary small" disabled={!text.trim() || answer.isPending} onClick={() => answer.mutate()}>
              {answer.isPending ? 'Enviando…' : 'Responder'}
            </button>
          </div>
          {(answer.error || remove.error) && <ErrorBox error={answer.error ?? remove.error} />}
        </div>
      )}
    </div>
  );
}

const STATUS: Record<string, string> = {
  CLOSED_UNANSWERED: 'anúncio encerrado sem resposta', UNDER_REVIEW: 'em revisão no ML', BANNED: 'moderada pelo ML', DELETED: 'apagada', DISABLED: 'desativada',
};

function TemplatesDialog({ templates, max, onClose }: { templates: Template[]; max: number; onClose: () => void }) {
  const qc = useQueryClient();
  const [edit, setEdit] = useState<Partial<Template> | null>(null);
  useEffect(() => { if (!templates.length) setEdit({ title: '', body: '' }); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const refresh = () => qc.invalidateQueries({ queryKey: ['answer-templates'] });
  const save = useMutation({
    mutationFn: (t: Partial<Template>) => api(t.id ? `/channels/questions/templates/${t.id}` : '/channels/questions/templates', {
      method: t.id ? 'PUT' : 'POST', json: { title: t.title, body: t.body, position: t.position ?? templates.length },
    }),
    onSuccess: () => { setEdit(null); void refresh(); },
  });
  const remove = useMutation({ mutationFn: (id: string) => api(`/channels/questions/templates/${id}`, { method: 'DELETE' }), onSuccess: () => refresh() });
  return (
    <Modal title="Respostas prontas" onClose={onClose}>
      <p className="small muted" style={{ margin: 0 }}>Use <span className="code">{'{produto}'}</span>, <span className="code">{'{preco}'}</span>, <span className="code">{'{estoque}'}</span> e <span className="code">{'{marca}'}</span>: são trocados pelos dados do anúncio da pergunta.</p>
      {edit ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <div className="field"><label htmlFor="tpl-title">Nome</label><input id="tpl-title" className="input" value={edit.title ?? ''} onChange={(e) => setEdit({ ...edit, title: e.target.value })} placeholder="Ex.: Tem estoque / Prazo de entrega" /></div>
          <div className="field"><label htmlFor="tpl-body">Texto</label><textarea id="tpl-body" className="input" rows={5} maxLength={max} value={edit.body ?? ''} onChange={(e) => setEdit({ ...edit, body: e.target.value })}
            placeholder="Olá! Sim, temos {produto} em estoque, pronto para envio. Qualquer dúvida, estamos à disposição." /></div>
          {save.error && <ErrorBox error={save.error} />}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            {templates.length > 0 && <button type="button" className="btn" onClick={() => setEdit(null)}>Cancelar</button>}
            <button type="button" className="btn primary" disabled={!edit.title?.trim() || !edit.body?.trim() || save.isPending} onClick={() => save.mutate(edit)}>Salvar</button>
          </div>
        </div>
      ) : (
        <>
          <div style={{ display: 'grid', gap: 6, maxHeight: 360, overflow: 'auto' }}>
            {templates.map((t) => (
              <div key={t.id} className="panel small" style={{ padding: '8px 10px', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, minWidth: 0 }}><b>{t.title}</b><div className="muted" style={{ whiteSpace: 'pre-wrap' }}>{t.body}</div></div>
                <button type="button" className="btn small ghost" aria-label="Editar" onClick={() => setEdit(t)}><Pencil size={14} /></button>
                <button type="button" className="btn small ghost" aria-label="Apagar" onClick={() => { if (window.confirm(`Apagar "${t.title}"?`)) remove.mutate(t.id); }}><Trash2 size={14} /></button>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <button type="button" className="btn" onClick={() => setEdit({ title: '', body: '' })}><Plus size={14} /> Nova resposta pronta</button>
            <button type="button" className="btn primary" onClick={onClose}>Concluir</button>
          </div>
        </>
      )}
    </Modal>
  );
}
